import type { FC } from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    BooleanSearchContainer,
    FooterButton,
    BooleanSearchWrapStyled,
    ImportFileLink,
    SpanInputMeasure,
    InlineInputSection,
} from "./segmentRulesStyles";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { i18nFilter } from "app/filters/i18nFilter";
import type { ISegmentFilter, IRule } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { BooleanSearchInputWrap, BooleanSearchInput } from "@similarweb/ui-components";
import { SWReactIcons } from "@similarweb/icons";
import { Button } from "@similarweb/ui-components";
import { ImportCSVModal } from "pro-features/components/Rule/src/RuleModes/EditRule/EditRuleModals/ImportCSVModal";
import { RuleSelectedValuesRow } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/RuleSelectedValuesRow";
import { addItems, removeItems } from "app/pages/segments/redesigned-wizard/segment-wizard/segmentWizardHelpers";
import { allTrackers } from "app/services/track/track";

const UPDATE_PREVIEW_DEBOUNCE = 600;

export const ExactUrlsFilter: FC<ISegmentFilter> = ({ rule, onApply, filterType, selectedTab }) => {
    const [currentRule, setCurrentRule] = useState<IRule>(null);
    const [exactUrlsValue, setExactUrlsValue] = useState("");
    const [exactUrlsInput, setExactUrlsInput] = useState(exactUrlsValue);
    const [applyWhenReady, setApplyWhenReady] = useState(false);
    const [isOpenImportModal, setIsOpenImportModal] = useState(false);
    const inputRef = useRef(null);
    const spanInputMeasureRef = useRef(null);
    const buttonsSectionRef = useRef(null);

    useEffect(() => {
        const componentRule = { ...rule };
        // Spread is a shallow copy, and I want to fully copy only the exactURLS,
        // as the rest of the rule filters will remain unchanged while editing the current rule
        componentRule.exactURLS = [...rule.exactURLS];
        setCurrentRule(componentRule);
    }, []);

    const hasUserInput = useMemo(() => {
        return exactUrlsValue && exactUrlsValue.trim().length > 0;
    }, [exactUrlsValue]);

    const onAddExactURLS = useCallback(
        (exactURLS: string[]) => {
            const updatedRule = addItems({ exactURLS }, currentRule);
            setCurrentRule(updatedRule);
        },
        [currentRule],
    );

    const onRemoveExactURLS = useCallback(
        (exactURLS: string[]) => {
            const updatedRule = removeItems("exactURLS", exactURLS, currentRule);
            setCurrentRule(updatedRule);
        },
        [currentRule],
    );

    const onApplyFilter = useCallback(
        () => onApply(currentRule, filterType, selectedTab),
        [onApply, currentRule, filterType, selectedTab],
    );

    /**
     * Updates the user input.
     * This will trigger an update of the urls preview, by useEffect on the customStringValue.
     */
    const changeExactUrlsTimeoutRef = useRef(null);
    const changeExactUrlsValue = useCallback(
        (newExactUrlValue, immediate = false) => {
            newExactUrlValue = decodeURIComponent(newExactUrlValue)
                ?.replace("http://www.", "")
                ?.replace("https://www.", "")
                ?.replace("http://", "")
                ?.replace("https://", "");
            const updateExactUrlsValue = () => {
                setExactUrlsValue(newExactUrlValue);
            };

            setExactUrlsInput(newExactUrlValue);
            clearTimeout(changeExactUrlsTimeoutRef.current);
            if (immediate) {
                updateExactUrlsValue();
            } else {
                changeExactUrlsTimeoutRef.current = setTimeout(updateExactUrlsValue, UPDATE_PREVIEW_DEBOUNCE);
            }
        },
        [setExactUrlsInput, exactUrlsValue],
    );

    const handleExactUrlsInput = useCallback(
        (evt) => {
            changeExactUrlsValue(evt.target.value);
        },
        [changeExactUrlsValue],
    );

    const handleExactUrlKeyUp = useCallback(
        (evt) => {
            switch (evt.key) {
                case "Enter":
                    setApplyWhenReady(true);
                    changeExactUrlsValue(evt.target.value, true);
                    break;
                case "Escape":
                    changeExactUrlsValue("", true);
                    break;
                default:
                    break;
            }
        },
        [changeExactUrlsValue, setApplyWhenReady],
    );

    const handleApplyUrl = useCallback(async () => {
        // prevent adding a word when in progress or no urls
        if (!hasUserInput) {
            return;
        }

        onAddExactURLS([exactUrlsValue]);

        // Reset user input to the initial state
        changeExactUrlsValue("", true);
    }, [exactUrlsValue, onAddExactURLS, changeExactUrlsValue]);

    const openImportFileModal = useCallback(() => {
        setIsOpenImportModal(true);
    }, []);

    const closeImportFileModal = useCallback(() => {
        setIsOpenImportModal(false);
    }, []);

    const handleImportStrings = useCallback(
        (strings) => {
            onAddExactURLS(strings);
        },
        [onAddExactURLS],
    );

    // Note: apply the current custom string when flag is true  and urls preview is ready
    useEffect(() => {
        if (applyWhenReady) {
            setApplyWhenReady(false);
            handleApplyUrl();
        }
    }, [applyWhenReady]);

    // calculate input text width to place the ADD button just afterwards.
    useLayoutEffect(() => {
        spanInputMeasureRef.current.textContent = exactUrlsInput;
        const textContentWidth = spanInputMeasureRef.current.offsetWidth;
        const posLeft = exactUrlsInput
            ? Math.min(textContentWidth, inputRef.current.offsetWidth) // place after text or max at the end of input
            : inputRef.current.offsetWidth; // if empty then place at the end of input
        buttonsSectionRef.current.style.left = `${posLeft}px`;
        buttonsSectionRef.current.style.paddingRight = `${inputRef.current.offsetWidth - posLeft}px`; // pad right to the button to avoid input click
    }, [exactUrlsInput]);

    const handleDownloadSampleCsv = useCallback(() => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            "segments/wizard/new/ExactUrl/upload/modal/download/csv/sample",
        );
    }, []);

    return (
        <FlexColumn>
            <ImportCSVModal
                isOpen={isOpenImportModal}
                onClose={closeImportFileModal}
                onImportSuccess={handleImportStrings}
                onDownloadSampleCsvClick={handleDownloadSampleCsv}
                modalTitle={"segmentWizard.csvModal.title.importExactUrls"}
            />
            {currentRule?.exactURLS?.length > 0 && (
                <RuleSelectedValuesRow
                    isFilterMode={true}
                    title={i18nFilter()("segmentWizard.editRule.exact.urls.row.title")}
                    valuesList={currentRule.exactURLS}
                    onRemoveValues={onRemoveExactURLS}
                />
            )}
            <BooleanSearchContainer>
                <BooleanSearchWrapStyled marginRight={"100px"}>
                    <SWReactIcons
                        size="sm"
                        iconName="add"
                    />
                    <BooleanSearchInputWrap>
                        <BooleanSearchInput
                            ref={inputRef}
                            placeholder={i18nFilter()("segmentWizard.editRule.exact.urls.modal.input")}
                            value={exactUrlsInput}
                            onChange={handleExactUrlsInput}
                            onKeyUp={handleExactUrlKeyUp}
                            autoFocus={true}
                        />
                        <SpanInputMeasure ref={spanInputMeasureRef} />
                        <InlineInputSection ref={buttonsSectionRef}>
                            {exactUrlsInput && (
                                <Button
                                    className="inlineButton"
                                    type="outlined"
                                    onClick={handleApplyUrl}
                                    isLoading={false}
                                    isDisabled={!hasUserInput}
                                >
                                    {i18nFilter()("segmentWizard.editRule.custom.string.modal.button")}
                                </Button>
                            )}
                        </InlineInputSection>
                    </BooleanSearchInputWrap>
                </BooleanSearchWrapStyled>
                <ImportFileLink
                    onClick={openImportFileModal}
                    type="outlined"
                    iconName="download"
                >
                    {i18nFilter()("segmentWizard.redesigned.editRule.exact.urls.modal.button.importStrings")}
                </ImportFileLink>
            </BooleanSearchContainer>
            <FooterButton
                onClick={onApplyFilter}
                isEnabled={currentRule?.exactURLS?.length > 0}
            >
                {i18nFilter()("common.apply")}
            </FooterButton>
        </FlexColumn>
    );
};
